import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useApiTranslations} from '../../../../context/ApiTranslationsContext';
import {SortingUtils} from '../../../../utils/sorting.utils';
import {FeatureTemplate} from '../../../../model/license/feature-template.model';
import {LicenseDetails} from '../../../../model/license/license-details.model';
import {OrganizationData} from '../../../../model/organization/organization-data';
import {OrganizationUserModel} from '../../../../model/organization/organization-user.model';
import {OrganizationUsersPageHooks} from './organization-users-page-hooks';

export type OrganizationUsersPageState = {
  users: OrganizationUserModel[]
  organizationDetails: OrganizationData | null
  licenseDetails: LicenseDetails | null

  getFeatureDetailsId: (featureTemplateId: string) => string | null

  mainTableRows: FeatureTemplate[]
  getChildRows: (row: FeatureTemplate) => FeatureTemplate[];
  isUserLicensePermissionDrawerOpen: boolean
  setUserLicensePermissionDrawerOpen: (value: boolean) => void
  isUserPermissionDrawerOpen: boolean
  setUserPermissionDrawerOpen: (value: boolean) => void

  selectedUserName: string | null
  selectedUserId: string | null
  setSelectedUserId: (value: string | null) => void

  translateLocal: (key: string) => string
  translate: (key: string) => string
  translateHeader: (key: string) => string
}

export const useOrganizationUsersPageState = (organizationId: string): OrganizationUsersPageState => {
  const {t} = useTranslation()
  const {translate: apiTranslate} = useApiTranslations()

  const [organizationUsers, ] = OrganizationUsersPageHooks.useOrganizationUsers(organizationId)
  const [organizationData, ] = OrganizationUsersPageHooks.useOrganizationDetails(organizationId)
  const [licenseDetails, ] = OrganizationUsersPageHooks.useLicenseDetails(organizationId);
  const [featureTemplates, ] = OrganizationUsersPageHooks.useFeatureTemplates();
  const [licenseFeatureDetails, ] = OrganizationUsersPageHooks.useLicenseFeatureDetails(organizationId);

  const [userLicensePermissionDrawerOpen, setUserLicensePermissionDrawerOpen] = useState<boolean>(false)
  const [userPermissionDrawerOpen, setUserPermissionDrawerOpen] = useState<boolean>(false)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const licenseFeatureTemplateIds = licenseFeatureDetails.map(it => it.featureTemplateId)

  return {
    selectedUserName: selectedUserId === null ? null : organizationUsers.find(it => it.id === selectedUserId)?.email ?? null,
    selectedUserId: selectedUserId,
    setSelectedUserId: setSelectedUserId,
    isUserLicensePermissionDrawerOpen: userLicensePermissionDrawerOpen,
    setUserLicensePermissionDrawerOpen: setUserLicensePermissionDrawerOpen,
    isUserPermissionDrawerOpen: userPermissionDrawerOpen,
    setUserPermissionDrawerOpen: setUserPermissionDrawerOpen,
    users: organizationUsers,
    organizationDetails: organizationData,
    licenseDetails: licenseDetails,
    getFeatureDetailsId: (featureTemplateId) => licenseFeatureDetails.find(it => it.featureTemplateId === featureTemplateId)?.id ?? null,
    mainTableRows: SortingUtils.sortFeatureTemplatesByModulePriority(
      SortingUtils.createModuleParents(
        featureTemplates.filter((it) => {
          const details = licenseFeatureDetails.find(licenseFeature => licenseFeature.featureTemplateId === it.id) ?? null
          return licenseFeatureTemplateIds.includes(it.id) && (details === null ? true : details.featureStatus === 'ACTIVE')
        })
      )
    ),
    getChildRows: (parent) => featureTemplates.filter((it) => {
      const details = licenseFeatureDetails.find(licenseFeature => licenseFeature.featureTemplateId === it.id) ?? null
      return it.featureModule === parent.featureModule && licenseFeatureTemplateIds.includes(it.id) && (details === null ? true : details.featureStatus === 'ACTIVE')
    }),
    translateLocal: (key) => t(`organization-users.${key}`),
    translate: (key) => apiTranslate(key) || t(key),
    translateHeader: (key) => t(`organizations-details.${key}`)
  }
}